/* eslint-disable react/prop-types */
import { endpoints } from '@/api/endpoints/endpoints';
import AsyncDataTable from '@/components/core/async-data-table/async-data-table';
import {
  DatableColumnType,
  FilterType,
  propertyType,
} from '@/components/core/async-data-table/type';
import { CashOnDeliveryBadge } from '@/components/orders/CashOnDeliveryBadge';
import { PrintStatusBadge } from '@/components/orders/PrintStatusBadge';
import { OrderStatusBadge } from '@/components/sale/OrderStatusBadge';
import { convertDateYYYYMMDD } from '@/constants/constant-function';
import { useBoolean } from '@/hooks/use-boolean';
import { useBreakpointReady } from '@/hooks/use-breakpoint-ready';
import { OrderStatus } from '@/types/order-type';
import { fDateTime } from '@/utils/datetime-util';
import { Dialog } from '@mui/material';
import { Plus, User2Icon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import OrderDetailPage from '../order-detail-page';
import { MobileOrderListView } from './mobile-order-list-view';
import OrderFilterHeader from './order-filter-header';

interface OrderAsyncTableProps {
  customerId?: string;
  headerAction?: () => void;
  headerActionText?: string;
  headerActionIcon?: React.ReactNode;
  showPageHeader?: boolean;
  pageTitle?: string;
  pageSubtitle?: string;
  refreshPage?: boolean;
}

const OrderAsyncTable = ({
  customerId,
  headerAction: customHeaderAction,
  headerActionText: customHeaderActionText,
  headerActionIcon: customHeaderActionIcon,
  showPageHeader = false,
  pageTitle = 'Orders',
  pageSubtitle = 'Manage your orders',
  refreshPage = false,
}: OrderAsyncTableProps = {}) => {
  const [, setLocation] = useLocation();
  const { isMobile, ready } = useBreakpointReady();

  const isOpenOrderDetailDialog = useBoolean(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  // Callbacks
  const handleCreateOrder = useCallback(() => {
    if (customHeaderAction) {
      customHeaderAction();
    } else {
      setLocation('/orders/new');
    }
  }, [setLocation, customHeaderAction]);

  const handleClickRow = useCallback((row: any) => {
    setSelectedOrderId(row.id);
    isOpenOrderDetailDialog.onTrue();
  }, []);

  // Column Definitions
  const cols: DatableColumnType[] = useMemo(() => [
    {
      name: 'orderNumber',
      id: 'order_number',
      label: 'Number',
      searchable: true,
      orderable: true,
      datatype: 'string' as const,
      Render: ({ row }: { row: any }) => {
        const orderNumber = row.orderNumber || '';
        return (
          <span className="block text-left font-mono font-semibold text-sm">
            {orderNumber}
          </span>
        );
      },
    },
    {
      name: 'customerName',
      id: 'customer_name',
      label: 'Customer',
      searchable: true,
      orderable: true,
      datatype: 'string' as const,
      Render: ({ row, isHighlighted }: { row: any; isHighlighted?: boolean }) => (
        <div className={`flex flex-col min-w-[100px] ${isHighlighted ? 'bg-blue-100 px-2 py-1 rounded' : ''}`}>
          <span className={`font-semibold text-sm truncate ${isHighlighted ? 'text-blue-700' : ''}`}>
            {row.customerName}
          </span>
        </div>
      ),
    },
    {
      name: 'status',
      id: 'status',
      label: 'Status',
      searchable: true,
      orderable: true,
      datatype: 'select' as const,
      selectArray: [
        { id: OrderStatus.DRAFT, text: 'Draft' },
        { id: OrderStatus.CONFIRMED, text: 'Confirmed' },
        { id: OrderStatus.PAID, text: 'Paid' },
        { id: OrderStatus.RETURNED, text: 'Returned' },
        { id: OrderStatus.CANCELLED, text: 'Cancelled' },
        { id: OrderStatus.REFUNDED, text: 'Refunded' },
      ],
      Render: ({ row }: { row: any }) => (
        <OrderStatusBadge status={row.status} cod={row.cashOnDelivery} />
      ),
    },
    {
      name: 'cashOnDelivery',
      id: 'cash_on_delivery',
      label: 'Cash on Delivery',
      searchable: false,
      orderable: false,
      Render: ({ row }: { row: any }) => (
        <CashOnDeliveryBadge isCashOnDelivery={row?.cashOnDelivery} />
      ),
    },
    {
      name: 'lastPrintUser',
      id: 'last_print_user',
      label: 'Print',
      Render: ({ row }: { row: any }) => (
        <div className="flex flex-col gap-1">
          <PrintStatusBadge username={row.lastPrintUser} printDate={row.lastPrintTimestamp} />
          {row.followUpUser && (
            <div className="flex items-center gap-1.5">
              <User2Icon className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-slate-700">{row.followUpUser}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      name: 'totalAmount',
      id: 'total_amount',
      label: 'Total',
      orderable: true,
      Render: ({ row }: { row: any }) => (
        <span className="font-bold text-sm text-indigo-700">
          ${parseFloat(row.totalAmount || 0).toFixed(2)}
        </span>
      ),
    },
    {
      name: 'totalItems',
      id: 'total_items',
      label: 'Items',
      datatype: 'number' as const,
      orderable: true,
    },
    {
      name: 'updatedAt',
      id: 'updated_at',
      label: 'Date',
      Render: ({ row }: { row: any }) => <div className="text-sm whitespace-nowrap">{fDateTime(row.updatedAt)}</div>,
    },
  ], []);

  const defaultFilterVals: propertyType[] = useMemo(() => {
    const filters: propertyType[] = [];

    // Add customer filter if provided
    if (customerId) {
      filters.push({ propName: 'customer_id', value: customerId, type: FilterType.STRING });
      return filters;
    }

    // Add today's date filter as default (orders list only)
    const today = new Date();
    const todayFormatted = convertDateYYYYMMDD(today);
    if (todayFormatted) {
      filters.push({ propName: 'created_at', value: todayFormatted, type: FilterType.DATE });
    }

    return filters;
  }, [customerId]);

  if (!ready) {
    return (
      <div className="async-data-table-container flex min-h-[200px] items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          <span className="text-sm text-gray-500">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="async-data-table-container">
      {/* Desktop: table (only mount when not mobile to avoid duplicate API) */}
      {!isMobile && (
        <AsyncDataTable
          dataUrl={endpoints.order.list}
          columns={cols}
          autoIndex={true}
          FilterComponent={OrderFilterHeader}
          refreshPage={refreshPage}
          defaultFilterProp={defaultFilterVals}
          defaultFilterVals={defaultFilterVals}
          orderByDef="total_amount"
          orderDef="desc"
          showPageHeader={showPageHeader}
          pageTitle={pageTitle}
          pageSubtitle={pageSubtitle}
          headerAction={customHeaderAction || handleCreateOrder}
          headerActionText={customHeaderActionText || 'New Order'}
          headerActionIcon={customHeaderActionIcon || <Plus size={18} />}
          onClickRow={handleClickRow}
          fetchSize={5000}
          highlightedRowId={selectedOrderId}
        />
      )}

      {/* Mobile: cards (only mount when mobile) */}
      {isMobile && (
        <MobileOrderListView
          dataUrl={endpoints.order.list}
          defaultFilterVals={defaultFilterVals}
          FilterComponent={OrderFilterHeader}
          filterColumn={cols.filter((e) => e.searchable === true)}
          showPageHeader={showPageHeader}
          pageTitle={pageTitle}
          refreshPage={refreshPage}
          pageSubtitle={pageSubtitle}
          headerAction={customHeaderAction || handleCreateOrder}
          headerActionText={customHeaderActionText || 'New Order'}
          headerActionIcon={customHeaderActionIcon || <Plus size={18} />}
          onOrderClick={handleClickRow}
          highlightedOrderId={selectedOrderId}
        />
      )}

      {selectedOrderId && (
        <Dialog
          open={isOpenOrderDetailDialog.value}
          onClose={() => {
            isOpenOrderDetailDialog.onFalse();
          }}
          fullScreen
        >
          <OrderDetailPage
            orderId={selectedOrderId}
            onClose={() => {
              isOpenOrderDetailDialog.onFalse();
            }}
          />
        </Dialog>
      )}
    </div>
  );
};

export default OrderAsyncTable;
